import { EMPTY, of } from "rxjs";
import {
  catchError,
  filter,
  ignoreElements,
  map,
  mergeMap,
  tap,
} from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import { ErrorResponseMessages } from "@/enums/ErrorResponseMessages.enum";
import {
  documentMetadata,
  selectCanUseProjects,
} from "../../state/config/config.selector";
import { userToken } from "../../state/user/user.selector";
import { selectDashboardSelectedProject } from "../../state/workspaces/projects.selector";
import type { RoleUser, WorkspaceRoles } from "../../state/workspaces/types";
import type { UpsertWorkspaceUsersParameters } from "../../state/workspaces/workspaces.actions";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import {
  isCreditLimitReached,
  selectWorkspacesSearchLabelsString,
  selectWorkspacesSearchTerm,
} from "../../state/workspaces/workspaces.selector";
import { Localized } from "../../strings";
import { isErrorResponse } from "../../utils/error.utils";
import { isAjaxError } from "../dependencies/ajaxRequests";
import { waitForToken } from "../helpers/waitForState";
import type { AppEpic } from "../types";
import { globalAccessToWorkspaceRole } from "./workspaces.utils";

// entry point - load project data once we have populated the workspaces
export const autoUpdateProjectsEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(isActionOf([workspacesActions.getProjects])),
    filter(() => selectCanUseProjects(state$.value)),
    mergeMap(() =>
      of(
        workspacesActions.getProjectsOfUser.request(),
        workspacesActions.assignableProjects.request(),
      ),
    ),
  );

export const getAssignableProjectsEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.assignableProjects.request)),
    waitForToken(state$),
    mergeMap((requestAction) =>
      documentIdv3Requests
        .getProjectsMyAssignable({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
        })
        .pipe(
          map((response) =>
            workspacesActions.assignableProjects.success(response),
          ),
          catchError((error) =>
            of(
              workspacesActions.assignableProjects.failure({
                requestAction,
                error,
                message: Localized.string("ERROR.SOMETHING_WENT_WRONG"),
              }),
            ),
          ),
        ),
    ),
  );

export const getProjectsOfUserEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.getProjectsOfUser.request)),
    waitForToken(state$),
    mergeMap((requestAction) =>
      documentIdv3Requests
        .getProjectsMy({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
        })
        .pipe(
          map((response) =>
            workspacesActions.getProjectsOfUser.success(response),
          ),
          catchError((error) =>
            of(
              workspacesActions.getProjectsOfUser.failure({
                requestAction,
                error,
                message: Localized.string("ERROR.SOMETHING_WENT_WRONG"),
              }),
            ),
          ),
        ),
    ),
  );

export const addWorkspaceToProjectEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.addWorkspaceToProject.request)),
    mergeMap((action) =>
      documentIdv3Requests
        .putProjectsProjectWorkspaces({
          origin: documentMetadata(state$.value),
          projectId: action.payload.projectId,
          token: userToken(state$.value),
          workspaceIds: [action.payload.workspaceId],
        })
        .pipe(
          map(() =>
            workspacesActions.addWorkspaceToProject.success(action.payload),
          ),
          catchError((error) => {
            let message = Localized.string("ERROR.CANNOT_APPLY_CHANGES"); // generic error
            if (isAjaxError(error)) {
              console.dir(error);
              switch (error.status) {
                case 400:
                  message = [
                    Localized.string("ERROR.PROJECTS.CANNOT_ADD_WORKSPACE"),
                    isErrorResponse(error.response)
                      ? error.response.code ===
                        ErrorResponseMessages.WorkspaceLimitReached
                        ? Localized.string(
                            "ERROR.PROJECTS.REASON_MAX_WORKSPACES",
                          )
                        : error.response.message
                      : String(error.response),
                  ].join(" ");
                  break;
                case 403:
                  message = Localized.string(
                    "ERROR.PROJECTS.INSUFFICIENT_PERMISSIONS_TO_ADD_WORKSPACE",
                    String(error.response),
                  );
                  break;
              }
            }
            return of(
              workspacesActions.addWorkspaceToProject.failure({
                requestAction: action,
                error,
                message,
              }),
            );
          }),
        ),
    ),
  );

export const reloadPageAfterAddWorkspaceToProjectSuccessEpic: AppEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.addWorkspaceToProject.success)),
    filter(() => isCreditLimitReached(state$.value)),
    tap(() => {
      window.location.reload();
    }),
    ignoreElements(),
  );

export const removeWorkspaceFromProjectEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.removeWorkspaceFromProject.request)),
    mergeMap((action) =>
      documentIdv3Requests
        .deleteProjectsProjectWorkspace({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          ...action.payload,
        })
        .pipe(
          map(() =>
            workspacesActions.removeWorkspaceFromProject.success(
              action.payload,
            ),
          ),
          catchError((error) => {
            let message = Localized.string("ERROR.CANNOT_APPLY_CHANGES"); // generic error
            if (isAjaxError(error) && error.status === 403) {
              message = Localized.string(
                "ERROR.PROJECTS.INSUFFICIENT_PERMISSIONS_TO_REMOVE_WORKSPACE",
              );
            }
            return of(
              workspacesActions.removeWorkspaceFromProject.failure({
                requestAction: action,
                error,
                message,
              }),
            );
          }),
        ),
    ),
  );

/**
 * When duplicating a workspace from a project -> reload the selected project's workspaces.
 */
export const updateWorkspacesOfProjectOnDuplicateWorkspaceEpic: AppEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.duplicateWorkspace.success)),
    // Only re-populate project workspaces if there is an active selected project.
    map(() => selectDashboardSelectedProject(state$.value)?.id),
    filter((projectId) => projectId !== undefined),
    map((projectId) =>
      workspacesActions.getWorkspacesOfProject.request({
        labels: selectWorkspacesSearchLabelsString(state$.value),
        name: selectWorkspacesSearchTerm(state$.value),
        projectId,
      }),
    ),
  );

/**
 * When removing a workspace from a project -> reload the selected project's workspaces.
 */
export const updateWorkspacesOfProjectOnRemoveWorkspaceEpic: AppEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.removeWorkspaceFromProject.success)),
    // Only re-populate project workspaces if there is an active selected project.
    map(() => selectDashboardSelectedProject(state$.value)?.id),
    filter((projectId) => projectId !== undefined),
    map((projectId) =>
      workspacesActions.getWorkspacesOfProject.request({
        labels: selectWorkspacesSearchLabelsString(state$.value),
        name: selectWorkspacesSearchTerm(state$.value),
        projectId,
      }),
    ),
  );

/**
 * When deleting a workspace > reload the selected project's workspaces.
 */
export const updateWorkspacesOfProjectOnDeleteWorkspaceEpic: AppEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.deleteWorkspace.success)),
    // Only re-populate project workspaces if there is an active selected project.
    map(() => selectDashboardSelectedProject(state$.value)?.id),
    filter((projectId) => projectId !== undefined),
    // NOTE: We do not have a containerId in the action payload, so just reload from the project we currently have loaded.
    map((projectId) =>
      workspacesActions.getWorkspacesOfProject.request({
        labels: selectWorkspacesSearchLabelsString(state$.value),
        name: selectWorkspacesSearchTerm(state$.value),
        projectId,
      }),
    ),
  );

// NOTE: potential service load optimization: Use local caching and only do bulkDetail request on workspaces that have changed (lastModified has changed)
export const getWorkspacesOfProjectEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.getWorkspacesOfProject.request)),
    waitForToken(state$),
    mergeMap((action) =>
      documentIdv3Requests
        .getProjectWorkspaces({
          origin: documentMetadata(state$.value),
          token: userToken(state$.value),
          ...action.payload,
        })
        .pipe(
          map((response) =>
            workspacesActions.getWorkspacesOfProject.success(response),
          ),
          catchError((error) =>
            of(
              workspacesActions.getWorkspacesOfProject.failure({
                requestAction: action,
                error,
                message: Localized.string("ERROR.SOMETHING_WENT_WRONG"),
              }),
            ),
          ),
        ),
    ),
  );

const toLowerCaseRoleUserEmailString = (roleUser: RoleUser) =>
  roleUser.email?.toLowerCase();

function getUsersToAddToWorkspacePermissions(
  roles: WorkspaceRoles,
  projectUsers: string[],
) {
  const caseInsensitiveRoles = new Set(
    [roles.administrators, roles.readers, roles.writers, roles.unspecifiedUsers]
      .flat()
      .map(toLowerCaseRoleUserEmailString),
  );

  const alreadyExists = (email: string) =>
    caseInsensitiveRoles.has(email.toLowerCase());

  const globalAccess = roles.globalAccess;

  const usersToAdd: Omit<UpsertWorkspaceUsersParameters, "workspaceId"> = {
    permission: globalAccessToWorkspaceRole(globalAccess),
    userEmails: projectUsers.filter((u) => !alreadyExists(u)),
  };

  return usersToAdd;
}

export const importProjectCollaboratorsEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests, log },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.getProjectCollaborators.request)),
    mergeMap((action) =>
      documentIdv3Requests
        .getProjectUsers(
          documentMetadata(state$.value),
          userToken(state$.value),
          action.payload,
        )
        .pipe(
          map((response) =>
            workspacesActions.getProjectCollaborators.success(response),
          ),
          catchError((e) => {
            log.error("Unable to import project users", e);
            return EMPTY;
          }),
        ),
    ),
  );

export const importProjectUsersToWorkspacePermissionsEpic: AppEpic = (
  action$,
  state$,
  { documentIdv3Requests, log },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.importProjectUsers)),
    mergeMap((action) =>
      documentIdv3Requests
        .getProjectUsers(
          documentMetadata(state$.value),
          userToken(state$.value),
          action.payload.containerId,
        )
        .pipe(
          map((response) => {
            const userEmails = response.map((u) => u.email);

            return workspacesActions.upsertWorkspaceUsers.request({
              ...getUsersToAddToWorkspacePermissions(
                action.payload.workspaceRoles,
                userEmails,
              ),
              workspaceId: action.payload.workspaceId,
            });
          }),
          catchError((e) => {
            log.error("Unable to import project users", e);
            return EMPTY;
          }),
        ),
    ),
  );

export const selectProjectEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(isActionOf(workspacesActions.selectProject)),
    filter((action) => !!action.payload?.id),
    mergeMap((action) => {
      const projectId = action.payload!.id;
      return of(
        workspacesActions.getWorkspacesOfProject.request({ projectId }),
        workspacesActions.getProjectCollaborators.request(projectId),
      );
    }),
  );
};

export const autoUpdateLogProjectIdEpic: AppEpic = (
  action$,
  _state$,
  { log },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.selectProject)),
    tap((action) => {
      // NOTE: a label cannot be set to undefined or null at the moment
      log.setLabels({ projectId: action.payload?.id ?? "" });
    }),
    ignoreElements(),
  );
